import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addFeedback, updateFeedback, fetchFeedbacksForPatientEncounter } from "apiClients/feedback";
import { useEffect } from "react";
import { FeedbackPayload, FeedbackUpdateParams } from "./index.type";

export const useFeedBackMutation = () => {
  const mutationFeedback = useMutation({
    mutationFn: async (payload: FeedbackPayload) => {
      return addFeedback(payload);
    },
  });
  return { mutationFeedback };
};

export const useFeedBackUpdateMutation = () => {
  const mutationFeedback = useMutation({
    mutationFn: async ({ feedbackId, payload }: FeedbackUpdateParams) => {
      return updateFeedback(feedbackId, payload);
    },
  });
  return { mutationFeedback };
};

export const useFeedback = (patientId: string, encounterId: string) => {
  const queryClient = useQueryClient();
  const mutationFeedback = useMutation({
    mutationFn: async (payload: FeedbackPayload) => {
      return addFeedback(payload);
    },
    onSuccess: (newFeedback) => {
      // Update the cache directly without invalidating
      queryClient.setQueryData(["feedbacks", patientId, encounterId], (oldFeedbacks: any[] = []) => {
        return [...oldFeedbacks, newFeedback];
      });
    },
  });

  const {
    data: feedbackList,
    isLoading: isFetching,
    refetch,
  } = useQuery({
    queryKey: ["feedbacks", patientId, encounterId],
    staleTime: 10 * 60 * 1000, // 10 minutes stale time
    queryFn: ({ signal }) => {
      return fetchFeedbacksForPatientEncounter(patientId, encounterId, {
        signal,
      });
    },
    enabled: !!patientId,
  });

  useEffect(() => {
    // Refetch whenever keys change
    refetch();
    return () => {
      // Invalidate the query on unmount to trigger abort
      queryClient.invalidateQueries({ queryKey: ["feedbacks", patientId, encounterId] });
    };
  }, [queryClient, patientId, encounterId, refetch]);

  return { feedbackList, isFetching, mutationFeedback };
};
